import React from 'react'
import { GalleryPickerContainer } from './GalleryPicker'
import { Block } from '../Block'
import styles from './GalleryBlock.scss'
import { BlockCaption } from '../BlockCaption'
import { Postcard } from 'common/Postcard'
import { BlockOverlay } from '../BlockOverlay'
import { FullWidthImage } from './FullWidthImage'
import { GalleryPopoverContainer } from './GalleryPopover'
import { Gallery } from 'common/Gallery'
import { shallowCompareObjects } from 'lib/utils'
import { photoLarge } from 'lib/urlHelpers'
import { FormattedMessage as T } from 'react-intl'

// full-width image:
// - should now get its own component
// - records center of gravity as a percentage of the photo's height
// - handles small viewports without hitting https://github.com/ridewithgps/rwgps-ui/issues/374
export class GalleryBlock extends React.Component {
  constructor (props) {
    super(props)
    this.state = {
      showHelp: false,
      editingPostcard: false,
      reselectPhotos: false
    }
  }

  toggleHelp = () => {
    const { showHelp } = this.state
    this.setState({ showHelp: !showHelp })
  }

  stopEditing = () => {
    this.setState({ editingPostcard: false })
  }

  shouldComponentUpdate (nextProps, nextState) {
    const shouldUpdate = (
      !this.props.blockProps.photos.equals(nextProps.blockProps.photos) ||
      !this.props.block.equals(nextProps.block) ||
      !this.props.selection.equals(nextProps.selection) ||
      shallowCompareObjects(this.state, nextState)
    )
    return shouldUpdate
  }

  UNSAFE_componentWillReceiveProps (nextProps) { // eslint-disable-line
    const { isSelected } = nextProps.blockProps
    if (!isSelected && this.state.editingPostcard) {
      this.setState({ editingPostcard: false })
    }
  }

  reselectPhotos = () => {
    // have to manually hide since we eat the mouseDown event
    this.setState({ reselectPhotos: true, editingPostcard: false })
  }

  onImagesSelected = ({ imageIds, minAspect }) => {
    const data = this.props.block.getData()
    const prevIds = data.get('imageIds', [])
    if (prevIds.length === imageIds.length && prevIds.every((id, index) => id === imageIds[index])) {
      // if we're annotating an old galleryblock, set minAspect
      (minAspect !== data.get('minAspect')) && this.props.blockProps.onChange({ minAspect })
      // nothing to be done here, bail out
      this.setState({ reselectPhotos: false })
      return
    }

    // if only one image selected, default to that
    let defaultCaption
    if (imageIds.length === 1) {
      defaultCaption = this.props.blockProps.photos.find(p => p.get('id') === imageIds[0]).get('caption')
    }
    const caption = this.props.block.getData().get('caption', defaultCaption)
    this.setState({ reselectPhotos: false })
    this.props.blockProps.onChange({ imageIds, minAspect, postcardLayout: null, caption })
  }

  togglePostcard = () => {
    const displayAsPostcard = !this.props.block.getData().get('displayAsPostcard')
    this.props.blockProps.onChange({ displayAsPostcard })
  }

  // we are only allowing embiggening/shrinking for postcards currently, not the gallery
  embiggen = () => {
    this.props.blockProps.onChange({ size: 'bigBlock' })
  }

  shrink = () => {
    this.props.blockProps.onChange({ size: 'normalBlock' })
  }

  persistPostcardLayout = (postcardLayout) => {
    this.props.blockProps.onChange({ postcardLayout })
  }

  toggleEditPostcard = () => {
    const { editingPostcard } = this.state
    this.setState({ editingPostcard: !editingPostcard })
  }

  setFocusRef = (focusRef) => {
    this._focusRef = focusRef
  }

  onCenterChange = (yCenter) => {
    this.props.blockProps.onChange({ yCenter })
  }

  render () {
    const { reselectPhotos, editingPostcard, showHelp } = this.state
    const { blockProps, block } = this.props
    const { rides, photos, ridePhotos, reportPhotos, reportId, isSelected, resetEditorMode } = blockProps
    const data = this.props.block.getData()
    const imageIds = data.get('imageIds', [])
    const displayAsPostcard = data.get('displayAsPostcard') && imageIds.length > 1
    const postcardLayout = data.get('postcardLayout')
    const minAspect = data.get('minAspect')
    const canEmbiggen = displayAsPostcard || imageIds.length === 1
    const size = (canEmbiggen && !showHelp) ? data.get('size', 'normalBlock') : 'normalBlock'

    if (!imageIds || imageIds.length === 0 || reselectPhotos) {
      return (
        <Block blockProps={blockProps} block={block}>
          <GalleryPickerContainer
            resetEditorMode={resetEditorMode}
            reportId={reportId}
            rides={rides}
            photos={photos}
            ridePhotos={ridePhotos}
            reportPhotos={reportPhotos}
            onChange={this.onImagesSelected}
            defaultSelected={imageIds} />
        </Block>
      )
    }

    const postcardImageSpec = postcardLayout
      ? { layout: postcardLayout }
      : { srcs: imageIds.map(id => `/photos/large/${id}.jpg`) }
    // force the postcard to re-mount when editing or changing size
    const postcardKey = imageIds.join('-') + (editingPostcard ? '-edit' : '') + `-${size}`

    return (
      <Block size={size} blockProps={blockProps} block={block}>
        <div
          ref={this.setFocusRef}
          className={styles.GalleryBlock}
          onBlur={this.stopEditing}>
          {isSelected && <GalleryPopoverContainer
            reportId={reportId}
            reselectPhotos={this.reselectPhotos}
            displayAsPostcard={displayAsPostcard}
            editingPostcard={editingPostcard}
            canEmbiggen={canEmbiggen}
            size={size}
            imageIds={imageIds}
            toggleEditPostcard={this.toggleEditPostcard}
            embiggen={this.embiggen}
            shrink={this.shrink}
            togglePostcard={this.togglePostcard}
            toggleHelp={this.toggleHelp} />}
          {displayAsPostcard
            ? <Postcard
              key={postcardKey}
              disableFilmstrip
              edit={editingPostcard}
              {...postcardImageSpec}
              onChange={this.persistPostcardLayout} />
            : <GalleryOrSingleImage
              minAspect={minAspect}
              imageIds={imageIds}
              big={size === 'bigBlock'}
              yCenter={data.get('yCenter')}
              onCenterChange={this.onCenterChange} />
          }
          <BlockCaption block={this.props.block} blockProps={this.props.blockProps} />
        </div>
        {showHelp && <Help onClose={this.toggleHelp} />}
      </Block>
    )
  }
}

const GalleryOrSingleImage = ({ imageIds, big = false, yCenter, onCenterChange, minAspect }) => {
  if (imageIds.length === 1) {
    return big
      ? <FullWidthImage src={photoLarge(imageIds[0])} yCenter={yCenter} onCenterChange={onCenterChange} />
      : <div className={styles.singleImg}><img src={photoLarge(imageIds[0])} /></div>
  } else {
    return <Gallery minAspect={minAspect} imageIds={imageIds} onClick={() => {}} />
  }
}

const Help = ({ onClose }) => <BlockOverlay onClose={onClose}>
  <h1><T id='GalleryBlock.helpH1' defaultMessage='Gallery block' /></h1>
  <p><T id='GalleryBlock.helpP1' defaultMessage='Add a Gallery block to display photos from your ride.' /></p>
  <h2><T id='GalleryBlock.helpH2' defaultMessage='Selecting images' /></h2>
  <p>
    <T id='GalleryBlock.helpP2' defaultMessage='When you first add a Gallery block, you can select from photos associated with your ride or rides or you can drag and drop photos from your computer. Click on images from your ride or rides to include them in your gallery, and then hit “done.” You can go back and change your selections or add additional photos by clicking on the gallery and choosing “change photos” from the block menu.' />
  </p>
  <h2><T id='GalleryBlock.postcardMode' defaultMessage='Postcard mode' /></h2>
  <p>
    <T
      id='GalleryBlock.helpP3'
      defaultMessage='You can display your photos in a tiled layout instead of a slideshow-type gallery by switching to Postcard view. Click the gallery and choose “switch to postcard” from the block menu. Postcard mode works best with 6 photos or fewer. You can change the postcard photo placement and zoom levels by clicking “reposition photos.” Drag photos into new positions on the postcard or use the sliders to zoom in and out on photos.' />
  </p>
</BlockOverlay>
